import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, catchError, distinctUntilChanged, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { ProcessEventTriggerService as ProcessEventTriggerApiService } from '@6grain/masofa-monolith-api-client/common/api/processEventTrigger.service';
import { PrettyJsonPipe } from '@app/utils/pipes/pretty-json.pipe';
import { OrchestrationIdentityCellComponent } from '@app/pages/admin/components/orchestration-identity-cell/orchestration-identity-cell';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import {
  EventCatalogCard,
  EventExecutionView,
  ProcessEventCatalogItem,
  buildEventCatalog,
  findEventCatalogCard,
  sortEventExecutions,
  toEventExecutionView,
} from '@app/pages/admin/orchestration-events.util';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { toRecordPageHeading } from '@app/pages/admin/orchestration-record-heading.util';
import { NOTIFY_PEOPLE_PATH, notifyPeopleQueryParams } from '@app/pages/admin/orchestration-notify-people.util';

interface ProcessDefinition {
  key: string;
  displayName?: string;
}

@Component({
  selector: 'app-orchestration-event-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    TagModule,
    BackButton,
    Loading,
    SecondaryButton,
    DataTableComponent,
    PrettyJsonPipe,
    OrchestrationIdentityCellComponent,
    CopyableIdentifierComponent,
    OrchestrationTableHeadingComponent,
  ],
  templateUrl: './orchestration-event-page.html',
  styleUrl: './orchestration-event-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationEventPageComponent implements OnInit, OnDestroy {
  private readonly processesApi = inject(AssignmentProcessApiService);
  private readonly triggersApi = inject(ProcessEventTriggerApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<string>();

  private readonly triggerNameTemplate = viewChild<TemplateRef<unknown>>('triggerNameTemplate');
  private readonly triggerStatusTemplate = viewChild<TemplateRef<unknown>>('triggerStatusTemplate');
  private readonly triggerExecutionsTemplate = viewChild<TemplateRef<unknown>>('triggerExecutionsTemplate');
  private readonly lastPublisherTemplate = viewChild<TemplateRef<unknown>>('lastPublisherTemplate');
  private readonly lastStartedTemplate = viewChild<TemplateRef<unknown>>('lastStartedTemplate');
  private readonly executionStateTemplate = viewChild<TemplateRef<unknown>>('executionStateTemplate');
  private readonly executionEventIdTemplate = viewChild<TemplateRef<unknown>>('executionEventIdTemplate');
  private readonly executionPublisherTemplate = viewChild<TemplateRef<unknown>>('executionPublisherTemplate');
  private readonly executionStartedTemplate = viewChild<TemplateRef<unknown>>('executionStartedTemplate');

  readonly eventKey = signal('');
  readonly event = signal<EventCatalogCard | null>(null);
  readonly executions = signal<EventExecutionView[]>([]);
  readonly triggerSearch = signal('');
  readonly executionSearch = signal('');
  readonly loading = signal(false);

  readonly pageHeading = computed(() => toRecordPageHeading({
    meaning: this.event()?.displayName,
    key: this.event()?.key,
    fallbackTitle: 'Event details',
  }));
  readonly errorMessage = signal('');

  readonly visibleTriggers = computed(() =>
    filterByCatalogQuery(
      this.event()?.triggers,
      this.triggerSearch(),
      trigger => [
        trigger.name,
        trigger.statusLabel,
        trigger.sourceProcessKey,
        trigger.sourceProcessLabel,
        trigger.targetProcessKey,
        trigger.targetProcessLabel,
        ...trigger.identityLines.flatMap(line => [line.label, line.title]),
      ],
    ),
  );

  readonly visibleExecutions = computed(() =>
    filterByCatalogQuery(
      this.executions(),
      this.executionSearch(),
      execution => [
        execution.triggerName,
        execution.state,
        execution.eventId,
        execution.sourceAssignmentId,
        execution.targetAssignmentId,
        execution.error,
      ],
    ),
  );

  readonly triggerColumns = computed<TableColumn[]>(() => [
    {
      field: 'name',
      header: 'Trigger',
      type: 'custom',
      customTemplate: this.triggerNameTemplate(),
    },
    {
      field: 'statusLabel',
      header: 'Status',
      type: 'custom',
      customTemplate: this.triggerStatusTemplate(),
    },
    {
      field: 'executionCount',
      header: 'Executions',
      type: 'custom',
      customTemplate: this.triggerExecutionsTemplate(),
    },
    { field: 'lastTriggeredAt', header: 'Last run', type: 'date' },
    {
      field: 'lastSourceAssignmentId',
      header: 'Published by',
      type: 'custom',
      customTemplate: this.lastPublisherTemplate(),
    },
    {
      field: 'lastTargetAssignmentId',
      header: 'Started assignment',
      type: 'custom',
      customTemplate: this.lastStartedTemplate(),
    },
  ]);

  readonly executionColumns = computed<TableColumn[]>(() => [
    { field: 'startedAt', header: 'Started At', type: 'date' },
    { field: 'triggerName', header: 'Trigger', type: 'text' },
    {
      field: 'state',
      header: 'Status',
      type: 'custom',
      customTemplate: this.executionStateTemplate(),
    },
    {
      field: 'eventId',
      header: 'Identifier',
      type: 'custom',
      customTemplate: this.executionEventIdTemplate(),
    },
    {
      field: 'sourceAssignmentId',
      header: 'Published by',
      type: 'custom',
      customTemplate: this.executionPublisherTemplate(),
    },
    {
      field: 'targetAssignmentId',
      header: 'Started assignment',
      type: 'custom',
      customTemplate: this.executionStartedTemplate(),
    },
    { field: 'error', header: 'Error', type: 'text' },
  ]);

  readonly triggerTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No triggers listen to this event',
  };

  readonly executionTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No executions for this event',
  };

  ngOnInit(): void {
    this.reload$.pipe(
      switchMap(eventKey => {
        this.loading.set(true);
        this.errorMessage.set('');
        return forkJoin({
          events: this.triggersApi.processEventTriggersEventsGet(),
          triggers: this.triggersApi.processEventTriggersGet(),
          processes: this.processesApi.assignmentsProcessesGet(),
        }).pipe(
          switchMap(({ events, triggers, processes }) => {
            const card = findEventCatalogCard(
              buildEventCatalog(
                events as ProcessEventCatalogItem[],
                triggers,
                processes as ProcessDefinition[],
              ),
              eventKey,
            );
            this.event.set(card);
            const related = (card?.triggers ?? []).filter(trigger => !!trigger.id);
            if (!related.length) {
              return of<EventExecutionView[]>([]);
            }
            return forkJoin(related.map(trigger =>
              this.triggersApi.processEventTriggersTriggerIdExecutionsGet(trigger.id, 50).pipe(
                map(items => items.map(item => toEventExecutionView(item, trigger))),
                catchError(() => of<EventExecutionView[]>([])),
              ),
            )).pipe(
              map(groups => sortEventExecutions(groups.flat())),
            );
          }),
          finalize(() => this.loading.set(false)),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: executions => this.executions.set(executions),
      error: () => {
        this.errorMessage.set('Failed to load events');
        this.event.set(null);
      },
    });

    this.route.paramMap.pipe(
      map(params => params.get('eventKey') ?? ''),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(eventKey => {
      this.eventKey.set(eventKey);
      this.event.set(null);
      this.executions.set([]);
      if (eventKey) {
        this.reload$.next(eventKey);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  reload(): void {
    const eventKey = this.eventKey();
    if (eventKey) {
      this.reload$.next(eventKey);
    }
  }

  createTrigger(): void {
    void this.router.navigate(['/admin/orchestration/subscriptions'], {
      queryParams: { eventKey: this.eventKey(), createTrigger: '1' },
    });
  }

  notifyPeople(): void {
    const eventKey = this.eventKey();
    if (!eventKey) {
      return;
    }
    void this.router.navigate([NOTIFY_PEOPLE_PATH], {
      queryParams: notifyPeopleQueryParams({ eventKey }),
    });
  }
}
